/**
 * DescentWrapper
 *
 * Wraps each phase screen with a background that warms and deepens as the
 * user descends into the session, then gently returns to the surface at close.
 *
 * Phase 0 (Arrival):  cool, airy — the surface
 * Phase 3 (See):      warmest, most interior — candlelight at the centre
 * Phase 4 (Shift):    golden depth — the turning point
 * Phase 6 (Close):    returned, softer — surface again, but changed
 *
 * The shift is intentionally subtle — felt rather than seen.
 */
import { useTranslation } from 'react-i18next'


interface DescentWrapperProps {
  phase: number
  children: React.ReactNode
}

// Background colours mapped to each phase.
// Cool → warm as you descend, then easing back as you return.
const PHASE_BG: Record<number, string> = {
  0: '#FAF7F4',  // Arrival — cool-warm white, the surface
  1: '#F8F3EE',  // Ground  — barely shifted, settling in
  2: '#F5EFE7',  // Feel    — warmer, going inward
  3: '#F1E8DC',  // See     — deeper, candlelight register
  4: '#EDE3D4',  // Shift   — golden interior, the deepest point
  5: '#F2EAE0',  // Choose  — beginning the return, softer gold
  6: '#F6F0E9',  // Close   — back at the surface, but warmed by the journey
}

// Subtle radial vignette that intensifies at depth (phases 3–4).
// Gives a sense of interiority without being dramatic.
const PHASE_VIGNETTE: Record<number, string> = {
  0: 'radial-gradient(ellipse at center, transparent 60%, rgba(61,31,98,0.00) 100%)',
  1: 'radial-gradient(ellipse at center, transparent 60%, rgba(61,31,98,0.02) 100%)',
  2: 'radial-gradient(ellipse at center, transparent 55%, rgba(61,31,98,0.04) 100%)',
  3: 'radial-gradient(ellipse at center, transparent 50%, rgba(61,31,98,0.07) 100%)',
  4: 'radial-gradient(ellipse at center, transparent 45%, rgba(61,31,98,0.09) 100%)',
  5: 'radial-gradient(ellipse at center, transparent 55%, rgba(61,31,98,0.04) 100%)',
  6: 'radial-gradient(ellipse at center, transparent 60%, rgba(61,31,98,0.02) 100%)',
}

export default function DescentWrapper({ phase, children }: DescentWrapperProps) {
  const { t } = useTranslation()
  const bg = PHASE_BG[Math.min(phase, 6)] ?? PHASE_BG[0]
  const vignette = PHASE_VIGNETTE[Math.min(phase, 6)] ?? PHASE_VIGNETTE[0]

  return (
    <div
      style={{
        backgroundColor: bg,
        backgroundImage: vignette,
        minHeight: '100svh',
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        transition: 'background-color 1s ease, background-image 1s ease',
      }}
    >
      <div
        style={{
          textAlign: 'center',
          paddingTop: '0.75rem',
          paddingBottom: '0.25rem',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        <span
          style={{
            fontSize: '0.6rem',
            fontWeight: 500,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'rgba(61,31,98,0.25)',
          }}
        >
          {t('home.appName')}
        </span>
      </div>
      {children}
    </div>
  )
}
